import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {lastValueFrom, Observable, ReplaySubject, Subject} from "rxjs";
import {AzureProfileResponse, DropdownList, LoginRequest, ProfileDetails} from "../../../api/models";
import {AzureAuthRedirectUrls} from "../../../app.config";
import {EnumsResponse, UtilityResponse} from "../../../api/models/response/utility-response";
import {UtilityService} from "../../../api/services/utility.service";
import {AZURE_TOKEN_KEY, LocalStorageUtils, USER_ID} from "../../utils/methods/local-storage.utils";
import {UtilityUtils} from "../../utils/methods/utility.utils";
import {PortalTypeConst} from "../../utils/constant/common.const";

@Injectable({
  providedIn: 'root'
})
export class AppraiserUtilsService {

  private _profileInfo: ReplaySubject<AzureProfileResponse> = new ReplaySubject<AzureProfileResponse>(1);
  private _accessToken: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _profileDetail: ReplaySubject<ProfileDetails> = new ReplaySubject<ProfileDetails>(1);
  public _isUpdatePricingGrid: Subject<boolean> = new Subject<boolean>();

  constructor(private _httpClient: HttpClient, private _utilsService: UtilityService) {
  }

  private _stateDropdown: ReplaySubject<DropdownList[]> = new ReplaySubject<DropdownList[]>(1);

  set stateDropdown(value: DropdownList[]) {
    // Store the value
    this._stateDropdown.next(value);
  }

  private _utilityDropdown: ReplaySubject<UtilityResponse> = new ReplaySubject<UtilityResponse>(1);

  set utilityDropdown(value: UtilityResponse) {
    // Store the value
    this._utilityDropdown.next(value);
  }

  private _enumsDropdown: ReplaySubject<EnumsResponse> = new ReplaySubject<EnumsResponse>(1);

  set enumsDropdown(value: EnumsResponse) {
    // Store the value
    this._enumsDropdown.next(value);
  }
  /**
   * Setter & getter for utility Dropdown
   *
   */
  get profileInfo$(): Observable<AzureProfileResponse> {
    return this._profileInfo.asObservable();
  }

  get getAzureAccessToken$(): Observable<any> {
    return this._accessToken.asObservable();
  }

  get utilityDropdown$(): Observable<UtilityResponse> {
    return this._utilityDropdown.asObservable();
  }

  get pagesDropdown$(): Observable<EnumsResponse> {
    return this._enumsDropdown.asObservable();
  }

  set profileDetails(value: ProfileDetails | undefined) {
    // Store the value
    if (value && Object.keys(value).length) this._profileDetail.next(value);
  }

  get profileDetails$(): Observable<ProfileDetails> {
    return this._profileDetail.asObservable();
  }

  get stateDropdown$(): Observable<DropdownList[]> {
    return this._stateDropdown.asObservable();
  }

  getLoggedUserProfileInfo(): void {
    this.getInitialData();
    this._httpClient.get<AzureProfileResponse>(AzureAuthRedirectUrls.profileInfoUrl).subscribe({
      next: (response: AzureProfileResponse) => {
        this._profileInfo.next(response);
        if (LocalStorageUtils.getAccessToken(AZURE_TOKEN_KEY)) {
          const payload: LoginRequest = {};
          payload.contactNo = UtilityUtils.StringIsNullOrEmpty(response.mobilePhone as string);
          payload.email = UtilityUtils.StringIsNullOrEmpty(response.mail as string);
          payload.firstName = UtilityUtils.StringIsNullOrEmpty(response.givenName as string);
          payload.lastName = UtilityUtils.StringIsNullOrEmpty(response.surname as string);
          payload.azureAdRole = UtilityUtils.StringIsNullOrEmpty(response.jobTitle as string);
          payload.userAzureAdtoken = UtilityUtils.StringIsNullOrEmpty(LocalStorageUtils.getAccessToken(AZURE_TOKEN_KEY) as string);
          this._utilsService.login(payload).then(async (_): Promise<void> => {
            // _.data is "UserID"
            if (_.data) {
              LocalStorageUtils.setAccessToken(USER_ID, _.data);
              LocalStorageUtils.removeInfo(AZURE_TOKEN_KEY);
              await this.getUserProfile();
            }
          })
        }
      }
    });
  }

  // Call Default API
  public async getInitialData(): Promise<void> {
    this.getUtilityDropdown();
    this.getEnumsDropdown();
    this.getStateDropdown();
    await this.getUserProfile();
  }

  /**
   * Initial Dropdown Api
   * */
  getUtilityDropdown(): void {
    this._utilsService.getUtilityDropdownList().subscribe({
      next: (response): void => {
        if (response.data) {
          // Filter All Portal Type Except "CAPPS"
          response.data.portalTypeEnums = response.data.portalTypeEnums.filter((_: DropdownList) => _.id === PortalTypeConst.CAPPS);
          this.utilityDropdown = response.data;
        }
      }
    })
  }

  /**
   * Enums Dropdown Api
   * */
  getEnumsDropdown(): void {
    this._utilsService.getEnumsDropdownList().subscribe({
      next: (response): void => {
        if (response.data) {
          this.enumsDropdown = response.data;
        }
      }
    })
  }
  public refreshUtilityEndpoints(): void {
    this.getUtilityDropdown();
  }

  // getUserProfile(): void {
  //   if (LocalStorageUtils.getAccessToken(USER_ID)) {
  //     this._utilsService.getProfileDetailsById(LocalStorageUtils.getAccessToken(USER_ID) as string).subscribe({
  //       next: (response): void => {
  //         this.profileDetails = response.data;
  //       }
  //     })
  //   }
  // }
  async getUserProfile(): Promise<ProfileDetails> {
    const userId = LocalStorageUtils.getAccessToken(USER_ID);
    if (!userId) return Promise.resolve({} as ProfileDetails);
    try {
      const response = await lastValueFrom(this._utilsService.getProfileDetailsById(userId));
      this.profileDetails = response.data;
      return response.data as ProfileDetails;
    } catch {
      return ({} as ProfileDetails);
    }
  }

  getStateDropdown(): void {
    this._utilsService.getStateList().subscribe({
      next: (response): void => {
        this.stateDropdown = response;
      }
    })
  }

  setAzureAccessToken(token: any): void {
    this._accessToken.next(token);
  }
}
